import { useState, useEffect, useCallback } from 'react';
import inventoryService from '../../../services/inventoryService';
import { usePaginationState } from '../../../hooks/usePaginationState.js';
import { usePaginatedData } from '../../../hooks/usePaginatedData.js';

export const useProducts = (options = {}) => {
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Use pagination hook for products data management
    const paginationState = usePaginationState({
        defaultSort: 'name',
        defaultOrder: 'asc',
        defaultLimit: options.defaultLimit || 10,
        limitOptions: options.limitOptions || [10, 20, 50, 100]
    });

    const { data, pagination, loading, error, setError, refetch } = usePaginatedData(
        inventoryService.getProducts,
        paginationState
    );

    const loadProducts = async (params = {}) => {
        await refetch(params);
    };

    const loadCategoriesAndSuppliers = useCallback(async () => {
        try {
            const [categoriesData, suppliersData] = await Promise.all([
                inventoryService.getCategories({ limit: 1000 }), // Get all categories
                inventoryService.getSuppliers({ limit: 1000 })   // Get all suppliers
            ]);
            setCategories(categoriesData.data || categoriesData);
            setSuppliers(suppliersData.data || suppliersData);
        } catch (err) {
            setError(err.error || 'Failed to load categories and suppliers');
        }
    }, [setError]);

    const createProduct = async (productData) => {
        try {
            setActionLoading(true);
            await inventoryService.createProduct(productData);
            await refetch();
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to create product';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    };

    const updateProduct = async (id, productData) => {
        try {
            setActionLoading(true);
            await inventoryService.updateProduct(id, productData);
            await refetch();
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to update product';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    };

    const deleteProduct = async (id) => {
        // Confirmation is now handled by the UI component
        try {
            setActionLoading(true);
            await inventoryService.deleteProduct(id);
            await refetch();
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to delete product';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    };

    const adjustInventory = async (id, adjustmentData) => {
        try {
            setActionLoading(true);
            await inventoryService.adjustInventory(id, adjustmentData);
            await refetch();
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || 'Failed to adjust inventory';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    };

    // Load categories and suppliers on mount
    useEffect(() => {
        loadCategoriesAndSuppliers();
    }, [loadCategoriesAndSuppliers]);

    // Data is automatically loaded by usePaginatedData hook

    return {
        data,
        products: data,
        pagination,
        loading,
        error,
        setError,
        categories,
        suppliers,
        actionLoading,
        loadProducts,
        loadCategoriesAndSuppliers, // Expose this function for refreshing dropdowns
        createProduct,
        updateProduct,
        deleteProduct,
        adjustInventory,
        refetch,
        ...paginationState
    };
};